/* import __COLOCATED_TEMPLATE__ from './response-time-range-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import { isEmpty } from '@ember/utils';
import { taskFor } from 'ember-concurrency-ts';
import { type BarFilter } from '../../filters';
import type ReportingMetrics from 'embercom/services/reporting-metrics';

const SECONDS_IN = {
  minute: 60,
  hour: 3600,
  day: 3600 * 24,
};

interface Args {
  initiallyOpened: boolean;
  icon: any;
  type: string;
  name: string;
  selected: BarFilter;
  setSelected: (filterValues: string[], operator?: string) => void;
  replaceFilter: (type: string, values: any[], operator: string) => void;
  analyticsObject: string;
  removeFilter: () => void;
  isDisabled: boolean;
  attribute: any;
}

export default class ResponseTimeRangeFilter extends Component<Args> {
  @service appService: any;
  @service store: any;
  @service intl!: IntlService;
  @service intercomEventService: any;
  @service declare reportingMetrics: ReportingMetrics;

  @tracked label = this.currentLabel;
  // store the selection here so that the UI doesn't update until the drop down is closed
  @tracked selectedValue = this.startingValue;
  @tracked selectedUnit = this.startingUnit;
  @tracked operator = this.startingOperator;
  @tracked includeBotInboxTime = this.startingIncludeBotInboxTime;

  @tracked operatorSettings: any;
  @tracked reportingSettings;

  unitsHash = {
    1: {
      text: this.intl.t('reporting.flexible-filters.range.short-form.seconds'),
      fullTextKey: 'reporting.flexible-filters.range.long-form.seconds',
    },
    [SECONDS_IN.minute]: {
      text: this.intl.t('reporting.flexible-filters.range.short-form.minutes'),
      fullTextKey: 'reporting.flexible-filters.range.long-form.minutes',
    },
    [SECONDS_IN.hour]: {
      text: this.intl.t('reporting.flexible-filters.range.short-form.hours'),
      fullTextKey: 'reporting.flexible-filters.range.long-form.hours',
    },
    [SECONDS_IN.day]: {
      text: this.intl.t('reporting.flexible-filters.range.short-form.days'),
      fullTextKey: 'reporting.flexible-filters.range.long-form.days',
    },
  };

  units = Object.keys(this.unitsHash).map((unit: any) => {
    return {
      text: this.unitsHash[unit].text,
      value: parseInt(unit, 10),
    };
  });

  constructor(owner: any, args: Args) {
    super(owner, args);
    taskFor(this.loadOperatorBotInboxSettings).perform();
    this.reportingSettings = this.loadReportingSettings;
  }

  @task({ restartable: true }) *loadOperatorBotInboxSettings(): TaskGenerator<void> {
    this.operatorSettings = yield this.store.findRecord(
      'operator-bot-inbox-settings',
      this.appService.app.id,
    );
  }

  get loadReportingSettings() {
    return (
      this.store.peekAll('reporting/settings')?.firstObject ||
      this.store.queryRecord('reporting/settings', {})
    );
  }

  get currentFilterAttributes() {
    let attributes = this.reportingMetrics.attributesById;

    return Object.values(attributes).filter((attribute) =>
      attribute.id.startsWith(this.args.attribute.id),
    );
  }

  get excludeOooHours() {
    if (this.reportingSettings === undefined) {
      return false;
    }
    return this.reportingSettings?.excludeOooHours;
  }

  get startingIncludeBotInboxTime() {
    return !this.args.selected.field?.includes('excluding_bot_inbox');
  }

  get operatorLabelMappings() {
    return {
      gt: this.intl.t('reporting.flexible-filters.range.more-than'),
      lt: this.intl.t('reporting.flexible-filters.range.less-than'),
    };
  }

  get startingValue() {
    let values = this.args.selected.values;

    if (isEmpty(values) || !Array.isArray(values)) {
      return null;
    }

    let value = values[0] / this.startingUnit;
    return Math.round(value * 100) / 100;
  }

  get startingUnit() {
    let values = this.args.selected.values;

    if (isEmpty(values) || !Array.isArray(values)) {
      return 1;
    }
    let val = values[0];
    if (val >= SECONDS_IN.day) {
      return SECONDS_IN.day;
    } else if (val >= SECONDS_IN.hour) {
      return SECONDS_IN.hour;
    } else if (val >= SECONDS_IN.minute) {
      return SECONDS_IN.minute;
    } else {
      return 1;
    }
  }

  get labelForCurrentUnit() {
    return (
      this.fullText(this.unitsHash[this.selectedUnit]?.fullTextKey) || REPORTING_FILTER_SELECT_ALL
    );
  }

  fullText(fullTextKey: string) {
    return this.intl.t(fullTextKey, { count: this.selectedValue });
  }

  get startingOperator() {
    return this.args.selected.operator || 'gt';
  }

  get currentLabel() {
    switch (this.operator) {
      case 'gt':
      case 'lt': {
        return `${this.operatorLabelMappings[this.operator]} ${this.labelForCurrentUnit}`;
      }
    }

    return ''; // fallback value
  }

  get shouldDisplayBotInboxToggle() {
    return this.isBotSettingOn && this.doesFilterSupportsBotExclude;
  }

  get isBotSettingOn() {
    return this.operatorSettings?.assignConversationsToBots;
  }

  get doesFilterSupportsBotExclude() {
    return this.currentFilterAttributes.any((attribute) =>
      attribute.id.includes('excluding_bot_inbox'),
    );
  }

  get doesFilterSupportsOooHours() {
    return this.currentFilterAttributes.any((attribute) =>
      attribute.id.includes('in_office_hours'),
    );
  }

  get valuesForFilter() {
    let valuesOrPlaceholder = this.selectedValue ? [this.selectedValue * this.selectedUnit] : [0];
    return valuesOrPlaceholder;
  }

  get minWidthOfOverlay() {
    return this.shouldDisplayBotInboxToggle ? 250 : 232;
  }

  get newFilterAttributeId() {
    let attributeId;
    if (this.doesFilterSupportsBotExclude && !this.includeBotInboxTime) {
      attributeId =
        this.doesFilterSupportsOooHours && this.excludeOooHours
          ? `${this.args.attribute.id}.excluding_bot_inbox.in_office_hours`
          : `${this.args.attribute.id}.excluding_bot_inbox`;
    } else {
      attributeId =
        this.doesFilterSupportsOooHours && this.excludeOooHours
          ? `${this.args.attribute.id}.in_office_hours`
          : `${this.args.attribute.id}`;
    }

    // Unfortunately we need this code to accomodate first_response_time as it does not follow dot convention
    if (this.attributeName === 'first_response_time') {
      attributeId = attributeId.replace('.excluding_bot_inbox', '_excluding_bot_inbox');
      attributeId = attributeId.replace('.in_office_hours', '_in_office_hours');
    }

    return attributeId;
  }

  get attributeName() {
    let attributeId = this.args.attribute.id;
    return attributeId.split('.')[1];
  }

  @action
  updateSelectedUnit(id: number) {
    this.selectedUnit = id;
  }

  @action
  toggleIncludeBotInboxTime() {
    this.includeBotInboxTime = !this.includeBotInboxTime;
  }

  @action
  onClose() {
    if (!this.valuesForFilter.length && !this.operator) {
      return;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: `filtered_${this.attributeName}`,
      object: this.args.analyticsObject,
      filter_name: this.args.type,
      // Need to check if attributeType is passed as prop
      custom_field_type: 'string',
    });
    // need to clear the old type and add the new type
    this.args.replaceFilter(this.newFilterAttributeId, this.valuesForFilter, this.operator);
    this.label = this.currentLabel;
  }

  @action
  removeFilter() {
    this.selectedValue = null;
    this.operator = '';
    this.args.removeFilter();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::ResponseTimeRangeFilter': typeof ResponseTimeRangeFilter;
    'reporting/custom/chart-builder/filter-bar/response-time-range-filter': typeof ResponseTimeRangeFilter;
  }
}
